import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity,
  BarChart3,
  Building2,
  CalendarDays,
  CalendarRange,
  Check,
  Copy,
  Eye,
  FileText,
  KeyRound,
  Mail,
  MessageSquare,
  MousePointerClick,
  Search,
  ShieldCheck,
  Trash2,
  UserPlus,
  UserRound,
  Users,
  Zap
} from 'lucide-react';
import { useLanguage } from '../assets/js/LanguageContext';
import ActionMenu from './common/ActionMenu';
import Checkbox from './common/Checkbox';
import Loader from './common/Loader';
import Modal from './common/Modal';
import Pagination from './common/Pagination';
import SectionEyebrow from './common/SectionEyebrow';
import HomeSealsTab from './Admin-/HomeSealsTab';
import OngRequestsTab from './Admin-/OngRequestsTab';
import { trackEvent } from '../utils/analytics';
import '../assets/styles/DashboardList.css';
import '../assets/styles/TemplateBuilder.css';
import '../assets/styles/ProfileArea.css';
import '../assets/styles/AdminArea.css';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const PAGE_SIZE_KEY = 'tp-admin-page-size';

const paginate = (items, page, size) => items.slice((page - 1) * size, (page - 1) * size + size);

const readPageSize = () => {
  try {
    const saved = Number(localStorage.getItem(PAGE_SIZE_KEY));
    return PAGE_SIZE_OPTIONS.includes(saved) ? saved : PAGE_SIZE_OPTIONS[0];
  } catch (error) {
    return PAGE_SIZE_OPTIONS[0];
  }
};

const Admin = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();

  const [userType, setUserType] = useState(null);
  const [allSurveys, setAllSurveys] = useState([]);
  const [assignedSurveys, setAssignedSurveys] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [userAccessPermissions, setUserAccessPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState(null);

  const [activeSection, setActiveSection] = useState(null);
  const [activeStatsTab, setActiveStatsTab] = useState('overview');

  const [usageStats, setUsageStats] = useState(null);
  const [usageStatsLoading, setUsageStatsLoading] = useState(false);
  const [activityStats, setActivityStats] = useState(null);
  const [activityStatsLoading, setActivityStatsLoading] = useState(false);

  const [searchUsers, setSearchUsers] = useState('');
  const [searchAssignments, setSearchAssignments] = useState('');
  const [searchSurveys, setSearchSurveys] = useState('');
  const [usersPage, setUsersPage] = useState(1);
  const [assignmentsPage, setAssignmentsPage] = useState(1);
  const [surveysPage, setSurveysPage] = useState(1);
  const [pageSize, setPageSize] = useState(readPageSize);
  const [copiedPin, setCopiedPin] = useState(null);

  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedSurveys, setSelectedSurveys] = useState([]);
  const [assignSearch, setAssignSearch] = useState('');
  const [assignSaving, setAssignSaving] = useState(false);
  const [assignmentToDelete, setAssignmentToDelete] = useState(null);
  const [userToPromote, setUserToPromote] = useState(null);

  // has_permissions: 0-3 son planes y 4-5 roles de administración
  const levelLabel = (level) => {
    const value = Number(level) || 0;
    if (value >= 5) return t('account_role_superadmin');
    if (value === 4) return t('account_role_admin');
    return t(`profile_plan_${['free', 'basic', 'pro', 'business'][value]}`);
  };

  const apiFetch = useCallback((path, options = {}) => {
    const token = localStorage.getItem('token');
    return fetch(`${process.env.REACT_APP_API_URL}${path}`, {
      ...options,
      headers: { Authorization: `Bearer ${token}`, ...(options.headers || {}) }
    });
  }, []);

  const showNotice = useCallback((variant, title, text, after) => {
    setNotice({ variant, title, text, after });
  }, []);

  const closeNotice = () => {
    const after = notice && notice.after;
    setNotice(null);
    if (after) after();
  };

  const loadJson = useCallback(async (path) => {
    const response = await apiFetch(path);
    if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);
    return response.json();
  }, [apiFetch]);

  const loadUsageStats = useCallback(async () => {
    setUsageStatsLoading(true);
    try {
      setUsageStats(await loadJson('/admin/usage-stats'));
    } catch (error) {
      console.error('Error cargando estadisticas de uso:', error);
    } finally {
      setUsageStatsLoading(false);
    }
  }, [loadJson]);

  const loadActivityStats = useCallback(async () => {
    setActivityStatsLoading(true);
    try {
      setActivityStats(await loadJson('/admin/activity-stats'));
    } catch (error) {
      console.error('Error cargando estadisticas de actividad:', error);
    } finally {
      setActivityStatsLoading(false);
    }
  }, [loadJson]);

  const loadUserAccessPermissions = useCallback(async () => {
    try {
      setUserAccessPermissions(await loadJson('/admin/user-access-permissions'));
    } catch (error) {
      showNotice('warning', t('adm_error_load_permissions'), error.message);
    }
  }, [loadJson, showNotice, t]);

  const loadAllUsers = useCallback(async () => {
    try {
      setAllUsers(await loadJson('/admin/all-users'));
    } catch (error) {
      showNotice('warning', t('adm_error_load_users'), error.message);
    }
  }, [loadJson, showNotice, t]);

  const loadSurveys = useCallback(async (level) => {
    try {
      if (level === 5) setAllSurveys(await loadJson('/admin/all-surveys'));
      else setAssignedSurveys(await loadJson('/admin/assigned-surveys'));
    } catch (error) {
      showNotice('warning', level === 5 ? t('adm_error_load_surveys') : t('adm_error_load_assigned'), error.message);
    }
  }, [loadJson, showNotice, t]);

  const loadData = useCallback(async (level) => {
    setLoading(true);
    try {
      await Promise.all([
        loadSurveys(level),
        loadAllUsers(),
        loadUserAccessPermissions(),
        loadUsageStats()
      ]);
    } finally {
      setLoading(false);
    }
  }, [loadSurveys, loadAllUsers, loadUserAccessPermissions, loadUsageStats]);

  const checkUserPermissions = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      const response = await apiFetch('/usuario');
      if (!response.ok) {
        navigate('/login');
        return;
      }

      const userData = await response.json();
      const level = userData.has_permissions;

      if (level === 4 || level === 5) {
        setUserType(level);
        await loadData(level);
        return;
      }

      setLoading(false);
      showNotice(
        'warning',
        t('adm_title'),
        t('adm_no_permission').replace('{level}', level || 'N/A'),
        () => navigate('/dashboard')
      );
    } catch (error) {
      setLoading(false);
      showNotice('warning', t('adm_error_verify'), error.message, () => navigate('/login'));
    }
  }, [apiFetch, loadData, navigate, showNotice, t]);

  useEffect(() => {
    checkUserPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeStatsTab === 'activity' && !activityStats && !activityStatsLoading) {
      loadActivityStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStatsTab]);

  const adminSections = useMemo(() => ([
    { key: 'stats', label: t('adm_section_stats'), permissions: [5] },
    { key: 'users', label: t('adm_section_users'), permissions: [4, 5] },
    { key: 'surveys', label: t('adm_section_surveys'), permissions: [4, 5] },
    { key: 'ong', label: t('adm_section_ong'), permissions: [5] },
    { key: 'seals', label: t('adm_section_seals'), permissions: [5] }
  ]), [t]);

  const visibleSections = adminSections.filter((section) => userType && section.permissions.includes(userType));

  useEffect(() => {
    if (userType && !activeSection && visibleSections.length > 0) {
      setActiveSection(visibleSections[0].key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userType]);

  const surveys = userType === 5 ? allSurveys : assignedSurveys;

  const filteredUsers = useMemo(() => {
    const term = searchUsers.trim().toLowerCase();
    const list = Array.isArray(allUsers) ? allUsers : [];
    if (!term) return list;
    return list.filter((user) => (
      (user.name || '').toLowerCase().includes(term)
      || (user.email || '').toLowerCase().includes(term)
      || levelLabel(user.has_permissions).toLowerCase().includes(term)
    ));
  }, [allUsers, searchUsers, t]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredAssignments = useMemo(() => {
    const term = searchAssignments.trim().toLowerCase();
    const list = Array.isArray(userAccessPermissions) ? userAccessPermissions : [];
    if (!term) return list;
    return list.filter((item) => (
      (item.user_name || '').toLowerCase().includes(term)
      || (item.survey_title || '').toLowerCase().includes(term)
      || String(item.survey_pin || '').includes(term)
    ));
  }, [userAccessPermissions, searchAssignments]);

  const filteredSurveys = useMemo(() => {
    const term = searchSurveys.trim().toLowerCase();
    const list = Array.isArray(surveys) ? surveys : [];
    if (!term) return list;
    return list.filter((survey) => (
      (survey.titulo || '').toLowerCase().includes(term)
      || String(survey.pin || '').includes(term)
      || (survey.creator_name || '').toLowerCase().includes(term)
    ));
  }, [surveys, searchSurveys]);

  const accessCountByUser = useMemo(() => {
    const counts = {};
    (Array.isArray(userAccessPermissions) ? userAccessPermissions : []).forEach((item) => {
      counts[item.user_id] = (counts[item.user_id] || 0) + 1;
    });
    return counts;
  }, [userAccessPermissions]);

  const assignableSurveys = useMemo(() => {
    const term = assignSearch.trim().toLowerCase();
    const list = Array.isArray(allSurveys) ? allSurveys : [];
    if (!term) return list;
    return list.filter((survey) => (
      (survey.titulo || '').toLowerCase().includes(term) || String(survey.pin || '').includes(term)
    ));
  }, [allSurveys, assignSearch]);

  const usersTotalPages = Math.max(1, Math.ceil(filteredUsers.length / pageSize));
  const assignmentsTotalPages = Math.max(1, Math.ceil(filteredAssignments.length / pageSize));
  const surveysTotalPages = Math.max(1, Math.ceil(filteredSurveys.length / pageSize));
  const currentUsersPage = Math.min(usersPage, usersTotalPages);
  const currentAssignmentsPage = Math.min(assignmentsPage, assignmentsTotalPages);
  const currentSurveysPage = Math.min(surveysPage, surveysTotalPages);
  const pagedUsers = paginate(filteredUsers, currentUsersPage, pageSize);
  const pagedAssignments = paginate(filteredAssignments, currentAssignmentsPage, pageSize);
  const pagedSurveys = paginate(filteredSurveys, currentSurveysPage, pageSize);

  const changePageSize = (size) => {
    setPageSize(size);
    setUsersPage(1);
    setAssignmentsPage(1);
    setSurveysPage(1);
    try {
      localStorage.setItem(PAGE_SIZE_KEY, String(size));
    } catch (error) {
      console.error('No se pudo guardar el tamano de pagina:', error);
    }
  };

  const formatDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString(language === 'en' ? 'en-US' : 'es-ES');
  };

  const formatDateTime = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString(language === 'en' ? 'en-US' : 'es-ES', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const initials = (name) => (name || '?')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');

  const surveyStatus = (item) => {
    const today = new Date();
    const start = new Date(item.fecha_inicio);
    const end = new Date(item.fecha_fin);
    if (today < start) return 'upcoming';
    if (today <= end) return 'active';
    return 'finished';
  };

  const statusLabel = {
    upcoming: t('adm_status_upcoming'),
    active: t('adm_status_active'),
    finished: t('adm_status_finished')
  };

  const copyPin = async (pin) => {
    try {
      await navigator.clipboard.writeText(String(pin));
      setCopiedPin(pin);
      setTimeout(() => setCopiedPin(null), 1800);
    } catch (error) {
      console.error('No se pudo copiar el PIN:', error);
    }
  };

  const openAssignModal = (user) => {
    setSelectedUser(user);
    setSelectedSurveys([]);
    setAssignSearch('');
  };

  const closeAssignModal = () => {
    setSelectedUser(null);
    setSelectedSurveys([]);
    setAssignSearch('');
  };

  const toggleSurveySelection = (id, checked) => {
    setSelectedSurveys((prev) => (checked ? [...prev, id] : prev.filter((item) => item !== id)));
  };

  const handleSaveAssignments = async () => {
    if (!selectedUser || selectedSurveys.length === 0) return;
    setAssignSaving(true);
    try {
      const response = await apiFetch('/admin/assign-surveys', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: selectedUser.id_usuario, surveyIds: selectedSurveys })
      });

      if (!response.ok) throw new Error(t('adm_assign_error'));

      closeAssignModal();
      loadUserAccessPermissions();
      showNotice('confirm', t('adm_assign_surveys'), t('adm_assign_success'));
    } catch (error) {
      showNotice('warning', t('adm_assign_surveys'), error.message || t('adm_assign_error'));
    } finally {
      setAssignSaving(false);
    }
  };

  const confirmDeleteAssignment = async () => {
    if (!assignmentToDelete) return;
    const assignment = assignmentToDelete;
    setAssignmentToDelete(null);

    try {
      const response = await apiFetch('/admin/remove-survey-assignment', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: assignment.user_id, surveyId: assignment.survey_id })
      });

      if (!response.ok) throw new Error(t('adm_delete_error'));

      loadUserAccessPermissions();
      showNotice('confirm', t('adm_remove_access'), t('adm_delete_success'));
    } catch (error) {
      showNotice('warning', t('adm_remove_access'), error.message || t('adm_delete_error'));
    }
  };

  const confirmMakeAdmin = async () => {
    if (!userToPromote) return;
    const user = userToPromote;
    setUserToPromote(null);
    trackEvent('button_click', 'make_admin', { user_id: user.id_usuario });

    try {
      const response = await apiFetch('/admin/make-admin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id_usuario })
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || t('adm_admin_error'));
      }

      loadAllUsers();
      showNotice('confirm', t('adm_make_admin'), t('adm_admin_success'));
    } catch (error) {
      showNotice('warning', t('adm_make_admin'), error.message || t('adm_admin_error'));
    }
  };

  // Resultados y respuestas se ven en la misma pagina que usa el dueño del diagnostico
  const openResults = (survey) => navigate(`/dashboard/${survey.id_encuesta}?from=admin`);
  const openResponses = (survey) => navigate(`/dashboard/${survey.id_encuesta}?tab=respuestas&from=admin`);

  const paginationLabels = {
    nav: t('dash_pagination_label'),
    prev: t('dash_page_prev'),
    next: t('dash_page_next'),
    page: (n) => t('dash_page_n').replace('{n}', n),
    show: t('dash_page_show'),
    perPage: t('dash_page_per_page'),
    perPageLabel: t('adm_page_size_label')
  };

  const pageSummary = (from, shown, total) => t('dash_page_summary')
    .replace('{from}', from + 1)
    .replace('{to}', from + shown)
    .replace('{total}', total);

  const renderSparkline = (data = [], valueKey = 'value', gradientId = 'admSpark') => {
    if (!data || data.length === 0) {
      return <p className="tp-admin__empty-text">{t('adm_stats_no_data')}</p>;
    }

    const width = 600;
    const height = 120;
    const padding = 24;
    const maxValue = Math.max(...data.map((item) => Number(item[valueKey]) || 0), 1);
    const stepX = (width - padding * 2) / Math.max(data.length - 1, 1);

    const points = data.map((item, index) => {
      const value = Number(item[valueKey]) || 0;
      return {
        x: padding + index * stepX,
        y: height - padding - (value / maxValue) * (height - padding * 2),
        value,
        day: item.day
      };
    });

    const line = points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x.toFixed(1)} ${point.y.toFixed(1)}`).join(' ');
    const area = `${line} L ${points[points.length - 1].x.toFixed(1)} ${(height - padding).toFixed(1)} L ${points[0].x.toFixed(1)} ${(height - padding).toFixed(1)} Z`;

    return (
      <svg className="tp-admin__spark" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" role="img">
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#7C3AED" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#7C3AED" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={area} fill={`url(#${gradientId})`} />
        <path d={line} fill="none" stroke="#7C3AED" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
        {points.map((point, index) => (
          <circle key={index} cx={point.x} cy={point.y} r="3" fill="#fff" stroke="#7C3AED" strokeWidth="2">
            <title>{`${point.day}: ${point.value}`}</title>
          </circle>
        ))}
      </svg>
    );
  };

  const renderKpis = (items) => (
    <div className="tp-admin__kpis">
      {items.map((item) => (
        <article key={item.key} className="tp-admin__kpi">
          <span className="tp-admin__kpi-icon" aria-hidden="true">{item.icon}</span>
          <div className="tp-admin__kpi-body">
            <p className="tp-admin__kpi-value">{item.value}</p>
            <p className="tp-admin__kpi-label">{item.label}</p>
          </div>
        </article>
      ))}
    </div>
  );

  const overviewKpis = usageStats ? [
    { key: 'users', icon: <Users size={20} strokeWidth={1.9} />, value: usageStats.totals?.users ?? 0, label: t('adm_stats_total_users') },
    { key: 'active24', icon: <Activity size={20} strokeWidth={1.9} />, value: usageStats.active_users?.last_24h ?? 0, label: t('adm_stats_active_24h') },
    { key: 'active7', icon: <CalendarDays size={20} strokeWidth={1.9} />, value: usageStats.active_users?.last_7d ?? 0, label: t('adm_stats_active_7d') },
    { key: 'active30', icon: <CalendarRange size={20} strokeWidth={1.9} />, value: usageStats.active_users?.last_30d ?? 0, label: t('adm_stats_active_30d') },
    { key: 'logins', icon: <KeyRound size={20} strokeWidth={1.9} />, value: usageStats.totals?.logins ?? 0, label: t('adm_stats_total_logins') },
    { key: 'surveys', icon: <FileText size={20} strokeWidth={1.9} />, value: usageStats.totals?.surveys ?? 0, label: t('adm_stats_total_surveys') },
    { key: 'responses', icon: <MessageSquare size={20} strokeWidth={1.9} />, value: usageStats.totals?.responses ?? 0, label: t('adm_stats_total_responses') },
    { key: 'new', icon: <UserPlus size={20} strokeWidth={1.9} />, value: usageStats.new_users?.last_30d ?? 0, label: t('adm_stats_new_users_30d') }
  ] : [];

  const activityKpis = activityStats ? [
    { key: 'events', icon: <Zap size={20} strokeWidth={1.9} />, value: activityStats.totals?.events_30d ?? 0, label: t('adm_stats_events_30d') },
    { key: 'views', icon: <Eye size={20} strokeWidth={1.9} />, value: activityStats.totals?.page_views_30d ?? 0, label: t('adm_stats_pageviews_30d') },
    { key: 'clicks', icon: <MousePointerClick size={20} strokeWidth={1.9} />, value: activityStats.totals?.clicks_30d ?? 0, label: t('adm_stats_clicks_30d') }
  ] : [];

  const renderStats = () => (
    <>
      <div className="tp-dash__tabs tp-admin__subtabs" role="group" aria-label={t('adm_stats_title')}>
        {[['overview', t('adm_stats_tab_overview')], ['activity', t('adm_stats_tab_activity')]].map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={`tp-dash__tab ${activeStatsTab === key ? 'is-active' : ''}`.trim()}
            aria-pressed={activeStatsTab === key}
            onClick={() => setActiveStatsTab(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {activeStatsTab === 'overview' && (
        <>
          {usageStatsLoading && <div className="tp-admin__loading"><Loader /></div>}

          {!usageStatsLoading && !usageStats && <p className="tp-dash__no-match">{t('adm_stats_no_data')}</p>}

          {!usageStatsLoading && usageStats && (
            <>
              {renderKpis(overviewKpis)}

              <section className="tp-admin__card">
                <header className="tp-admin__card-head">
                  <h3 className="tp-admin__card-title">{t('adm_stats_logins_trend')}</h3>
                  <span className="tp-admin__card-hint">{t('adm_stats_last_30_days')}</span>
                </header>
                {renderSparkline(usageStats.logins_by_day, 'logins', 'admSparkLogins')}
              </section>

              <div className="tp-admin__tables">
                <section className="tp-admin__card">
                  <header className="tp-admin__card-head">
                    <h3 className="tp-admin__card-title">{t('adm_stats_top_users')}</h3>
                  </header>
                  {(usageStats.top_users || []).length === 0 ? (
                    <p className="tp-admin__empty-text">{t('adm_stats_no_data')}</p>
                  ) : (
                    <ul className="tp-admin__rows">
                      {usageStats.top_users.map((user) => (
                        <li key={user.id_usuario} className="tp-admin__row">
                          <span className="tp-admin__row-avatar" aria-hidden="true">{initials(user.name)}</span>
                          <div className="tp-admin__row-main">
                            <p className="tp-admin__row-title">{user.name}</p>
                            <p className="tp-admin__row-sub">
                              {user.email}
                              {user.organization ? ` - ${user.organization}` : ''}
                            </p>
                          </div>
                          <span className="tp-admin__badge">
                            {t('adm_stats_logins_count').replace('{count}', user.login_count)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>

                <section className="tp-admin__card">
                  <header className="tp-admin__card-head">
                    <h3 className="tp-admin__card-title">{t('adm_stats_recent_users')}</h3>
                  </header>
                  {(usageStats.recent_users || []).length === 0 ? (
                    <p className="tp-admin__empty-text">{t('adm_stats_no_data')}</p>
                  ) : (
                    <ul className="tp-admin__rows">
                      {usageStats.recent_users.map((user) => (
                        <li key={user.id_usuario} className="tp-admin__row">
                          <span className="tp-admin__row-avatar" aria-hidden="true">{initials(user.name)}</span>
                          <div className="tp-admin__row-main">
                            <p className="tp-admin__row-title">{user.name}</p>
                            <p className="tp-admin__row-sub">{user.email}</p>
                          </div>
                          <span className="tp-admin__row-date">{formatDateTime(user.last_login)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              </div>
            </>
          )}
        </>
      )}

      {activeStatsTab === 'activity' && (
        <>
          {activityStatsLoading && <div className="tp-admin__loading"><Loader /></div>}

          {!activityStatsLoading && !activityStats && <p className="tp-dash__no-match">{t('adm_stats_no_data')}</p>}

          {!activityStatsLoading && activityStats && (
            <>
              {renderKpis(activityKpis)}

              <section className="tp-admin__card">
                <header className="tp-admin__card-head">
                  <h3 className="tp-admin__card-title">{t('adm_stats_events_trend')}</h3>
                  <span className="tp-admin__card-hint">{t('adm_stats_last_30_days')}</span>
                </header>
                {renderSparkline(activityStats.events_by_day, 'events', 'admSparkEvents')}
              </section>

              <section className="tp-admin__card">
                <header className="tp-admin__card-head">
                  <h3 className="tp-admin__card-title">{t('adm_stats_hourly_heatmap')}</h3>
                  <span className="tp-admin__card-hint">{t('adm_stats_last_30_days')}</span>
                </header>
                <div className="tp-admin__heatmap">
                  {(() => {
                    const hours = activityStats.events_by_hour || [];
                    const maxHour = Math.max(...hours.map((hour) => hour.events), 1);
                    return hours.map((hour) => (
                      <div
                        key={hour.hour}
                        className="tp-admin__heat-cell"
                        style={{ background: `rgba(124, 58, 237, ${0.08 + (hour.events / maxHour) * 0.8})` }}
                        title={`${String(hour.hour).padStart(2, '0')}:00 - ${hour.events}`}
                      >
                        <span className="tp-admin__heat-hour">{String(hour.hour).padStart(2, '0')}</span>
                        <span className="tp-admin__heat-value">{hour.events}</span>
                      </div>
                    ));
                  })()}
                </div>
              </section>

              <div className="tp-admin__tables">
                <section className="tp-admin__card">
                  <header className="tp-admin__card-head">
                    <h3 className="tp-admin__card-title">{t('adm_stats_top_pages')}</h3>
                  </header>
                  {(activityStats.top_pages || []).length === 0 ? (
                    <p className="tp-admin__empty-text">{t('adm_stats_no_data')}</p>
                  ) : (
                    <ul className="tp-admin__rows">
                      {activityStats.top_pages.map((page, index) => (
                        <li key={index} className="tp-admin__row">
                          <span className="tp-admin__row-icon" aria-hidden="true">
                            <Eye size={16} strokeWidth={2} />
                          </span>
                          <div className="tp-admin__row-main">
                            <p className="tp-admin__row-title">
                              <code className="tp-admin__code">{page.event_name}</code>
                            </p>
                            <p className="tp-admin__row-sub">
                              {t('adm_stats_unique_count').replace('{count}', page.unique_users)}
                            </p>
                          </div>
                          <span className="tp-admin__badge">
                            {t('adm_stats_views_count').replace('{count}', page.views)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>

                <section className="tp-admin__card">
                  <header className="tp-admin__card-head">
                    <h3 className="tp-admin__card-title">{t('adm_stats_top_events')}</h3>
                  </header>
                  {(activityStats.top_events || []).length === 0 ? (
                    <p className="tp-admin__empty-text">{t('adm_stats_no_data')}</p>
                  ) : (
                    <ul className="tp-admin__rows">
                      {activityStats.top_events.map((event, index) => (
                        <li key={index} className="tp-admin__row">
                          <span className="tp-admin__row-icon" aria-hidden="true">
                            <MousePointerClick size={16} strokeWidth={2} />
                          </span>
                          <div className="tp-admin__row-main">
                            <p className="tp-admin__row-title">
                              <code className="tp-admin__code">{event.event_name}</code>
                            </p>
                            <p className="tp-admin__row-sub">
                              {t('adm_stats_unique_count').replace('{count}', event.unique_users)}
                            </p>
                          </div>
                          <span className="tp-admin__badge">
                            {t('adm_stats_clicks_count').replace('{count}', event.clicks)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              </div>
            </>
          )}
        </>
      )}
    </>
  );

  const renderUsers = () => (
    <>
      <section className="tp-admin__block">
        <header className="tp-admin__block-head">
          <h2 className="tp-admin__block-title">{t('adm_access_management')}</h2>
          <span className="tp-admin__block-count">{filteredUsers.length}</span>
        </header>

        {userType === 4 && (
          <p className="tp-admin__note">
            <strong>{t('adm_note')}</strong> {t('adm_note_type4_users')}
          </p>
        )}

        <div className="tp-dash__controls">
          <label className="tp-dash__search">
            <Search size={16} strokeWidth={2} aria-hidden="true" />
            <span className="tp-sr-only">{t('adm_search_users')}</span>
            <input
              type="search"
              placeholder={t('adm_search_users')}
              value={searchUsers}
              onChange={(e) => { setSearchUsers(e.target.value); setUsersPage(1); }}
            />
          </label>
        </div>

        {filteredUsers.length === 0 ? (
          <p className="tp-dash__no-match">{t('adm_empty_users')}</p>
        ) : (
          <ul className="tp-dash__list">
            {pagedUsers.map((user) => {
              const menuItems = [{
                key: 'assign',
                label: t('adm_assign_surveys'),
                icon: <FileText size={16} />,
                onClick: () => openAssignModal(user)
              }];

              if (userType === 5 && user.has_permissions < 4) {
                menuItems.push({
                  key: 'admin',
                  label: t('adm_make_admin'),
                  icon: <ShieldCheck size={16} />,
                  onClick: () => setUserToPromote(user)
                });
              }

              return (
                <li key={user.id_usuario} className="tp-dash__item">
                  <div className="tp-dash__main">
                    <div className="tp-dash__title-row">
                      <h3 className="tp-dash__title">{user.name}</h3>
                      <span className={`tp-admin__level is-l${user.has_permissions}`}>
                        {levelLabel(user.has_permissions)}
                      </span>
                    </div>
                    <div className="tp-dash__meta">
                      <span className="tp-dash__dates">
                        <Mail size={14} strokeWidth={2} aria-hidden="true" />
                        {user.email}
                      </span>
                      {user.organization && (
                        <span className="tp-dash__dates">
                          <Building2 size={14} strokeWidth={2} aria-hidden="true" />
                          {user.organization}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="tp-dash__progress">
                    <span className="tp-dash__responses">
                      <FileText size={14} strokeWidth={2} aria-hidden="true" />
                      {(accessCountByUser[user.id_usuario] || 0) === 1
                        ? t('adm_access_one')
                        : t('adm_access_count').replace('{count}', accessCountByUser[user.id_usuario] || 0)}
                    </span>
                  </div>

                  <div className="tp-dash__actions">
                    <button
                      type="button"
                      className="btn btn--outline btn--sm tp-dash__primary"
                      onClick={() => openAssignModal(user)}
                    >
                      <FileText size={15} strokeWidth={2} aria-hidden="true" />
                      {t('adm_assign_surveys')}
                    </button>
                    <ActionMenu label={t('dash_more_actions')} groups={[{ key: 'user', items: menuItems }]} />
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {filteredUsers.length > 0 && (
          <Pagination
            page={currentUsersPage}
            totalPages={usersTotalPages}
            onChange={setUsersPage}
            total={filteredUsers.length}
            pageSize={pageSize}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onPageSizeChange={changePageSize}
            summary={pageSummary((currentUsersPage - 1) * pageSize, pagedUsers.length, filteredUsers.length)}
            labels={paginationLabels}
          />
        )}
      </section>

      <section className="tp-admin__block">
        <header className="tp-admin__block-head">
          <h2 className="tp-admin__block-title">{t('adm_current_assignments')}</h2>
          <span className="tp-admin__block-count">{filteredAssignments.length}</span>
        </header>

        {userType === 4 && (
          <p className="tp-admin__note">
            <strong>{t('adm_note')}</strong> {t('adm_note_type4_assignments')}
          </p>
        )}

        <div className="tp-dash__controls">
          <label className="tp-dash__search">
            <Search size={16} strokeWidth={2} aria-hidden="true" />
            <span className="tp-sr-only">{t('adm_search_assignments')}</span>
            <input
              type="search"
              placeholder={t('adm_search_assignments')}
              value={searchAssignments}
              onChange={(e) => { setSearchAssignments(e.target.value); setAssignmentsPage(1); }}
            />
          </label>
        </div>

        {filteredAssignments.length === 0 ? (
          <p className="tp-dash__no-match">{t('adm_empty_assignments')}</p>
        ) : (
          <ul className="tp-dash__list">
            {pagedAssignments.map((assignment) => {
              const status = surveyStatus(assignment);
              return (
                <li key={`${assignment.user_id}-${assignment.survey_id}`} className="tp-dash__item tp-admin__row--simple">
                  <div className="tp-dash__main">
                    <div className="tp-dash__title-row">
                      <h3 className="tp-dash__title">{assignment.survey_title}</h3>
                      <span className={`tp-dash__status is-${status}`}>{statusLabel[status]}</span>
                    </div>
                    <div className="tp-dash__meta">
                      <span className="tp-dash__dates">
                        <UserRound size={14} strokeWidth={2} aria-hidden="true" />
                        {assignment.user_name}
                      </span>
                      <span className="tp-dash__pin">
                        {t('adm_pin')} <strong>{assignment.survey_pin}</strong>
                        <button
                          type="button"
                          className="tp-dash__copy"
                          onClick={() => copyPin(assignment.survey_pin)}
                          aria-label={t('dash_copy_pin')}
                          title={t('dash_copy_pin')}
                        >
                          {copiedPin === assignment.survey_pin
                            ? <Check size={14} strokeWidth={2.5} aria-hidden="true" />
                            : <Copy size={14} strokeWidth={2} aria-hidden="true" />}
                        </button>
                      </span>
                    </div>
                  </div>

                  <div className="tp-dash__actions">
                    <button
                      type="button"
                      className="btn btn--outline btn--sm tp-dash__primary tp-admin__remove"
                      onClick={() => setAssignmentToDelete(assignment)}
                    >
                      <Trash2 size={15} strokeWidth={2} aria-hidden="true" />
                      {t('adm_remove_access')}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {filteredAssignments.length > 0 && (
          <Pagination
            page={currentAssignmentsPage}
            totalPages={assignmentsTotalPages}
            onChange={setAssignmentsPage}
            total={filteredAssignments.length}
            pageSize={pageSize}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onPageSizeChange={changePageSize}
            summary={pageSummary((currentAssignmentsPage - 1) * pageSize, pagedAssignments.length, filteredAssignments.length)}
            labels={paginationLabels}
          />
        )}
      </section>
    </>
  );

  const renderSurveys = () => (
    <section className="tp-admin__block">
      <header className="tp-admin__block-head">
        <h2 className="tp-admin__block-title">{userType === 5 ? t('adm_all_surveys') : t('adm_assigned_surveys')}</h2>
        <span className="tp-admin__block-count">{filteredSurveys.length}</span>
      </header>

      <div className="tp-dash__controls">
        <label className="tp-dash__search">
          <Search size={16} strokeWidth={2} aria-hidden="true" />
          <span className="tp-sr-only">{t('adm_search_surveys')}</span>
          <input
            type="search"
            placeholder={t('adm_search_surveys')}
            value={searchSurveys}
            onChange={(e) => { setSearchSurveys(e.target.value); setSurveysPage(1); }}
          />
        </label>
      </div>

      {filteredSurveys.length === 0 ? (
        <p className="tp-dash__no-match">{t('adm_empty_surveys')}</p>
      ) : (
        <ul className="tp-dash__list">
          {pagedSurveys.map((survey) => {
            const status = surveyStatus(survey);
            return (
              <li key={survey.id_encuesta} className="tp-dash__item tp-admin__row--simple">
                <div className="tp-dash__main">
                  <div className="tp-dash__title-row">
                    <h3 className="tp-dash__title">{survey.titulo}</h3>
                    <span className={`tp-dash__status is-${status}`}>{statusLabel[status]}</span>
                  </div>
                  <div className="tp-dash__meta">
                    <span className="tp-dash__pin">
                      {t('adm_pin')} <strong>{survey.pin}</strong>
                      <button
                        type="button"
                        className="tp-dash__copy"
                        onClick={() => copyPin(survey.pin)}
                        aria-label={t('dash_copy_pin')}
                        title={t('dash_copy_pin')}
                      >
                        {copiedPin === survey.pin
                          ? <Check size={14} strokeWidth={2.5} aria-hidden="true" />
                          : <Copy size={14} strokeWidth={2} aria-hidden="true" />}
                      </button>
                    </span>
                    <span className="tp-dash__dates">
                      <UserRound size={14} strokeWidth={2} aria-hidden="true" />
                      {survey.creator_name}
                    </span>
                    <span className="tp-dash__dates">
                      <CalendarDays size={14} strokeWidth={2} aria-hidden="true" />
                      {formatDate(survey.fecha_inicio)} - {formatDate(survey.fecha_fin)}
                    </span>
                  </div>
                </div>

                <div className="tp-dash__actions">
                  <button
                    type="button"
                    className="btn btn--outline btn--sm tp-dash__primary"
                    onClick={() => openResults(survey)}
                  >
                    <BarChart3 size={15} strokeWidth={2} aria-hidden="true" />
                    {t('adm_view_results')}
                  </button>

                  <ActionMenu
                    label={t('dash_more_actions')}
                    groups={[{
                      key: 'survey',
                      items: [
                        {
                          key: 'results',
                          label: t('adm_view_results'),
                          icon: <BarChart3 size={16} />,
                          onClick: () => openResults(survey)
                        },
                        {
                          key: 'responses',
                          label: t('adm_view_responses'),
                          icon: <MessageSquare size={16} />,
                          onClick: () => openResponses(survey)
                        }
                      ]
                    }]}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {filteredSurveys.length > 0 && (
        <Pagination
          page={currentSurveysPage}
          totalPages={surveysTotalPages}
          onChange={setSurveysPage}
          total={filteredSurveys.length}
          pageSize={pageSize}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          onPageSizeChange={changePageSize}
          summary={pageSummary((currentSurveysPage - 1) * pageSize, pagedSurveys.length, filteredSurveys.length)}
          labels={paginationLabels}
        />
      )}
    </section>
  );

  if (loading) {
    return (
      <div className="home-container tp-dash tp-admin">
        <div className="dashboard-container tp-admin--center">
          <Loader />
        </div>
      </div>
    );
  }

  return (
    <div className="home-container tp-dash tp-admin">
      <div className="dashboard-container">
        <header className="tp-dash__head">
          <div className="tp-dash__heading">
            <SectionEyebrow>{t('adm_title')}</SectionEyebrow>
            <p className="tp-dash__lead">{userType === 5 ? t('adm_desc_type5') : t('adm_desc_type4')}</p>
          </div>
        </header>

        {visibleSections.length > 1 && (
          <div className="tp-dash__toolbar">
            <div className="tp-dash__tabs" role="group" aria-label={t('adm_subnav_label')}>
              {visibleSections.map((section) => (
                <button
                  key={section.key}
                  type="button"
                  className={`tp-dash__tab ${activeSection === section.key ? 'is-active' : ''}`.trim()}
                  aria-pressed={activeSection === section.key}
                  onClick={() => setActiveSection(section.key)}
                >
                  {section.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {userType === 5 && activeSection === 'stats' && renderStats()}
        {activeSection === 'users' && renderUsers()}
        {activeSection === 'surveys' && renderSurveys()}
        {userType === 5 && activeSection === 'ong' && (
          <section className="tp-admin__block">
            <OngRequestsTab onNotice={showNotice} />
          </section>
        )}
        {userType === 5 && activeSection === 'seals' && (
          <section className="tp-admin__block tp-admin__block--plain">
            <HomeSealsTab />
          </section>
        )}

        <Modal
          open={Boolean(selectedUser)}
          onOpenChange={(open) => { if (!open) closeAssignModal(); }}
          variant="edit"
          title={`${t('adm_assign_to')} ${selectedUser ? selectedUser.name : ''}`}
          description={selectedUser ? selectedUser.email : ''}
          cancelText={t('common_cancel')}
          confirmText={`${t('adm_assign_count')} (${selectedSurveys.length})`}
          confirmDisabled={selectedSurveys.length === 0 || assignSaving}
          onCancel={closeAssignModal}
          onConfirm={() => { handleSaveAssignments(); return false; }}
          size="lg"
        >
          <label className="tp-dash__search tp-admin__modal-search">
            <Search size={16} strokeWidth={2} aria-hidden="true" />
            <span className="tp-sr-only">{t('adm_assign_search')}</span>
            <input
              type="search"
              placeholder={t('adm_assign_search')}
              value={assignSearch}
              onChange={(e) => setAssignSearch(e.target.value)}
            />
          </label>

          {assignableSurveys.length === 0 ? (
            <p className="tp-admin__empty-text">{t('adm_assign_empty')}</p>
          ) : (
            <ul className="tp-admin__picker">
              {assignableSurveys.map((survey) => (
                <li key={survey.id_encuesta} className="tp-admin__picker-item">
                  <Checkbox
                    checked={selectedSurveys.includes(survey.id_encuesta)}
                    onChange={(e) => toggleSurveySelection(survey.id_encuesta, e.target.checked)}
                  >
                    <span className="tp-admin__picker-title">{survey.titulo}</span>
                    <span className="tp-admin__picker-sub">{t('adm_pin')} {survey.pin}</span>
                  </Checkbox>
                </li>
              ))}
            </ul>
          )}
        </Modal>

        <Modal
          open={Boolean(assignmentToDelete)}
          onOpenChange={(open) => { if (!open) setAssignmentToDelete(null); }}
          variant="delete"
          title={t('adm_remove_access')}
          description={assignmentToDelete
            ? `${assignmentToDelete.user_name} - ${assignmentToDelete.survey_title} (${t('adm_pin')} ${assignmentToDelete.survey_pin})`
            : ''}
          cancelText={t('common_cancel')}
          confirmText={t('common_delete')}
          onCancel={() => setAssignmentToDelete(null)}
          onConfirm={confirmDeleteAssignment}
        />

        <Modal
          open={Boolean(userToPromote)}
          onOpenChange={(open) => { if (!open) setUserToPromote(null); }}
          variant="confirm"
          title={t('adm_make_admin')}
          description={userToPromote ? `${t('adm_confirm_make_admin')} (${userToPromote.name})` : ''}
          cancelText={t('common_cancel')}
          confirmText={t('adm_make_admin')}
          onCancel={() => setUserToPromote(null)}
          onConfirm={confirmMakeAdmin}
        />



        <Modal
          open={Boolean(notice)}
          onOpenChange={(open) => { if (!open) closeNotice(); }}
          variant={notice ? notice.variant : 'info'}
          title={notice ? notice.title : ''}
          description={notice ? notice.text : ''}
          confirmText={t('common_understood')}
          onConfirm={closeNotice}
        />
      </div>
    </div>
  );
};

export default Admin;
